import { defineStore } from 'pinia'
import axios from '@/api/axios'
import { useSwal } from '@/composables'
import { extractClapError } from '@/helpers/errorHelper'
import { useCurrentGameStore } from './game/currentGame'
import { useQuestionStore } from './question/question'

export interface MediaGalleryConf {
  hideButton: boolean
  onlyImage?: boolean
  type?: string
  current?: string
}

interface State {
  count: number
  mediaStore: Media[]
  perPage: number
  totalBytes: number
  maxBytes: number | null
  mediaGallery: {
    open: boolean
    conf: MediaGalleryConf
  }
}

const MARK_SRC_MEDIA_TRANSFORMATION =
  '/upload/if_w_ne_1200_or_h_ne_850/w_1200,h_850,c_fill/if_end/'

const EMPTY_MEDIA = { src: '', format: '', url: '' }

function extractPublicId(mediaUrl = '') {
  if (!mediaUrl) return null
  const filename = mediaUrl.split('/').pop() || ''
  return filename.split('.')[0] || null
}

function mediaMatchesUrl(
  media: { src?: string; url?: string } | null | undefined,
  publicId: string
) {
  if (!media || !publicId) return false
  return Boolean(
    (media.url && media.url.includes(publicId)) ||
      (media.src && media.src.includes(publicId))
  )
}

function clearMediaField<T extends Record<string, any>>(
  obj: T | null | undefined,
  key: keyof T,
  publicId: string
) {
  if (!obj?.[key] || !mediaMatchesUrl(obj[key], publicId)) return
  obj[key] = { ...EMPTY_MEDIA } as T[keyof T]
}

function clearQuestionMediaRefs(
  questionDoc: Question | null | undefined,
  publicId: string
) {
  if (!questionDoc) return
  const q = questionDoc.question as Question['question'] & {
    questions?: Array<{ content: string; media?: Media }>
  }
  if (q) {
    clearMediaField(q, 'backgroundMedia', publicId)
    clearMediaField(q, 'markSrcMedia', publicId)
    q.answers?.forEach(answer => {
      if (!mediaMatchesUrl(answer.ansMedia, publicId)) return
      if (
        answer.ans &&
        answer.ansMedia?.src &&
        answer.ans === answer.ansMedia.src
      ) {
        answer.ans = ''
      }
      answer.ansMedia = { ...EMPTY_MEDIA }
    })
    q.questions?.forEach(item => {
      if (!mediaMatchesUrl(item.media, publicId)) return
      item.media = { ...EMPTY_MEDIA }
    })
  }
  clearMediaField(questionDoc, 'openMedia', publicId)
  clearMediaField(questionDoc, 'endMedia', publicId)
  clearMediaField(questionDoc as any, 'backgroundMedia', publicId)
  if (questionDoc.setting) {
    clearMediaField(questionDoc.setting as any, 'slidBackgroundMedia', publicId)
  }
}

function normalizeMarkSrcMedia(media: Media): Media {
  const url = media?.url || ''

  if (!url.includes('/upload/') || url.includes('if_w_ne_1200_or_h_ne_850')) {
    return media
  }

  return {
    ...media,
    url: url.replace('/upload/', MARK_SRC_MEDIA_TRANSFORMATION),
  }
}

export const useMediaStore = defineStore('media', {
  state: (): State => ({
    count: 0,
    mediaStore: [],
    perPage: 6,
    totalBytes: 0,
    maxBytes: null,
    mediaGallery: {
      open: false,
      conf: {
        hideButton: true,
        onlyImage: false,
        type: '',
      },
    },
  }),

  getters: {
    isHideButton: state => state.mediaGallery.conf.hideButton,
    isOnlyImage: state => state.mediaGallery.conf.onlyImage,
    type: state => state.mediaGallery?.conf?.type,
    current: state => state.mediaGallery.conf?.current,
    getMediaStore: state => state.mediaStore,
    getMediaCount: state => state.count,
    getPerPage: state => state.perPage,
    isMediaGalleryOpen: state => state.mediaGallery.open,
    getMediaGalleryConf: state => state.mediaGallery.conf,
  },

  actions: {
    chosenCallback(media: Media) {
      const router = this.router.currentRoute.value
      const { setGameSettingByKey } = useCurrentGameStore()
      const {
        answers,
        setAnswers,
        setCurrentQuestionKey,
        setQuestionByKey,
        question,
      } = useQuestionStore()

      const type = router.query.gallery as string
      const index = Number(router.query.ansIndex)
      const queIndex = Number(router.query.queIndex)

      // answer media
      if (type === 'ansMedia' && index !== undefined) {
        const newAnswers = answers.map((a, i) => {
          if (i === index) {
            a.ansMedia = media
            a.ans = media.src
          }
          return a
        })
        setAnswers(newAnswers)
        //add a window event to notify other components
        window.dispatchEvent(new CustomEvent('answers-media-updated'))
      } else if (type === 'matchingAnswerMedia' && index !== undefined) {
        const newAnswers = answers.map((a, i) => {
          if (i === index) {
            a.ansMedia = media
          }
          return a
        })
        setAnswers(newAnswers)
      } else if (type === 'matchingQuestionMedia' && queIndex !== undefined) {
        const questions = question.questions || []
        const newQuestions = [...questions]
        if (newQuestions[queIndex]) {
          newQuestions[queIndex] = {
            ...newQuestions[queIndex],
            media: media,
          }
        }
        setQuestionByKey('questions', newQuestions)
      } else if (type === 'markSrcMedia') {
        setQuestionByKey(
          type as keyof Question['question'],
          normalizeMarkSrcMedia(media)
        )
      } else if (type === 'backgroundMedia') {
        setQuestionByKey(type as keyof Question['question'], media)
      } else if (type === 'logo') {
        setGameSettingByKey('logo', media)
      } else if (type === 'openMedia' || type === 'endMedia') {
        setCurrentQuestionKey(type, media)
      }
    },
    setGalleryOpen(open: boolean) {
      this.mediaGallery.open = open
    },

    setGalleryConf(conf: MediaGalleryConf) {
      this.mediaGallery.conf = conf
    },

    setMedia(media: Media[]) {
      this.mediaStore = media
    },

    unshiftAndMaybePopMedia(newMedia: Media) {
      this.mediaStore.unshift(newMedia)
      if (this.mediaStore.length > this.perPage) {
        this.mediaStore.pop()
      }
    },

    setMediaCount(count: number) {
      this.count = count
    },

    updateMediaTitle({
      mediaId,
      newTitle,
    }: {
      mediaId: string
      newTitle: string
    }) {
      const media = this.mediaStore.find(m => m._id === mediaId)
      if (media) {
        media.title = newTitle
      }
    },

    deleteMedia(mediaId: string) {
      const removed = this.mediaStore.find(m => m._id === mediaId)
      this.mediaStore = this.mediaStore.filter(m => m._id !== mediaId)
      this.count--
      if (removed?.bytes) {
        this.totalBytes = Math.max(0, this.totalBytes - removed.bytes)
      }
    },

    clearMediaReferencesLocally(mediaUrl: string) {
      const publicId = extractPublicId(mediaUrl)
      if (!publicId) return

      const { currentQuestion } = useQuestionStore()
      clearQuestionMediaRefs(currentQuestion, publicId)

      const { game } = useCurrentGameStore()
      game.questions?.forEach(question => {
        clearQuestionMediaRefs(question, publicId)
      })

      if (game.setting) {
        clearMediaField(game.setting, 'logo', publicId)
        clearMediaField(game.setting, 'gameMedia', publicId)
        clearMediaField(game.setting, 'triviaMedia', publicId)
        clearMediaField(game.setting, 'winnersListMedia', publicId)
        clearMediaField(game.setting, 'winnersMedia', publicId)
        if (game.setting.sounds) {
          ;[
            'winnersMediaSound',
            'winnersListMediaSound',
            'genericMediaSound',
            'timerMediaSound',
            'inShowAnsMediaSound',
          ].forEach(key => {
            clearMediaField(game.setting.sounds, key, publicId)
          })
        }
      }

      window.dispatchEvent(new CustomEvent('answers-media-updated'))
    },

    async indexMedia({
      page = 1,
      perPage = 6,
      search = '',
      type = '',
    }: {
      page?: number
      perPage?: number
      search?: string
      type?: string
    }) {
      try {
        const params = new URLSearchParams()
        params.append('page', page.toString())
        params.append('perPage', perPage.toString())
        params.append('searchText', search)
        params.append('type', type)

        const {
          data: { media, count, totalBytes, maxBytes },
        } = await axios.get(`/media/?${params.toString()}`)

        this.setMediaCount(count)
        this.setMedia(media)
        this.totalBytes = Number(totalBytes) || 0
        this.maxBytes = typeof maxBytes === 'number' ? maxBytes : null

        return media
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async storeMedia(payload: Record<string, any>) {
      try {
        const { data: media } = await axios.post('/media', payload)
        this.unshiftAndMaybePopMedia(media)
        this.totalBytes += Number(media?.bytes) || 0
        return media
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async updateMediaTitleOnServer({
      mediaId,
      newTitle,
    }: {
      mediaId: string
      newTitle: string
    }) {
      try {
        const { data: updatedMedia } = await axios.patch(`/media/${mediaId}`, {
          title: newTitle,
        })

        this.updateMediaTitle({
          mediaId: updatedMedia._id,
          newTitle: updatedMedia.title,
        })

        return updatedMedia
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async getMediaUsage(mediaId: string) {
      try {
        const { data } = await axios.get<{
          games: { _id: string; name: string }[]
        }>(`/media/${mediaId}/usage`)
        return data.games || []
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },

    async deleteMediaFromServer(
      mediaId: string,
      {
        removeFromGames = false,
        mediaUrl,
      }: { removeFromGames?: boolean; mediaUrl?: string } = {}
    ) {
      try {
        const params = removeFromGames ? { removeFromGames: true } : undefined
        await axios.delete(`/media/${mediaId}`, { params })
        this.deleteMedia(mediaId)
        if (mediaUrl) {
          this.clearMediaReferencesLocally(mediaUrl)
        }
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },
  },
})
